import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import { Download, GitCompare, Database, FileStack, MinusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = { committed: "success", staged: "warning", failed: "danger", analyzing: "outline" };

function Metric({ label, value, sub, icon: Icon, testid, tone }) {
  return (
    <div className="card-flat p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /><span className="label-eyebrow text-[9px]">{label}</span></div>
      <div className={`font-mono text-xl font-bold ${tone || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export function ExportReconciliation() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/data-management/reconciliation-report").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/data-management/reconciliation-report.csv", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = "export_reconciliation_report.csv"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading reconciliation report…</div>;
  if (d.empty) return <div className="p-6 text-muted-foreground text-sm">No data to reconcile yet.</div>;

  const { current, imports } = d;
  return (
    <div className="space-y-4" data-testid="export-reconciliation">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground max-w-xl">
          Source-vs-platform comparison — explains exactly why the row count in a source file differs
          from what the platform stores and exports, with the reason for every skipped row.
          {d.count_basis && <span className="block mt-1 text-[10px]" data-testid="recon-count-basis">{d.count_basis}</span>}
        </p>
        <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="recon-download-csv-btn">
          <Download className="h-3.5 w-3.5" /> Download report CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Metric label="In platform / exported" value={formatNum(current.total)} icon={Database} testid="recon-stat-current"
          sub={`${formatNum(current.environment)} Env · ${formatNum(current.social_governance)} S&G`} />
        <Metric label="Source rows analyzed" value={formatNum(imports.analyzed)} icon={FileStack} testid="recon-stat-analyzed"
          sub={`${imports.runs_committed} committed import run${imports.runs_committed === 1 ? "" : "s"}`} />
        <Metric label="Committed from imports" value={formatNum(imports.committed)} icon={GitCompare} testid="recon-stat-committed"
          sub={`${formatNum(imports.surviving_in_platform)} still in platform · ${formatNum(imports.deleted_since_import)} deleted since`} />
        <Metric label="Skipped at import" value={formatNum(imports.skipped)} icon={MinusCircle} testid="recon-stat-skipped"
          tone={imports.skipped ? "text-[#FFB800]" : ""} sub="with reasons below" />
        <Metric label="Deleted after import" value={formatNum(imports.deleted_since_import)} icon={Trash2} testid="recon-stat-deleted"
          tone={imports.deleted_since_import ? "text-red-400" : ""} sub="removed by users since commit" />
      </div>

      {d.plan_status && (
        <div className="card-flat p-3 border-accent/30 flex items-center gap-2 text-[11px]" data-testid="recon-plan-status">
          <span className="label-eyebrow text-[10px]">Plan status</span>
          <Badge variant={d.plan_status.status === "active" ? "success" : d.plan_status.status === "trialing" ? "warning" : "outline"} className="text-[9px]">
            {d.plan_status.status || "—"}{d.plan_status.plan_code ? ` · ${d.plan_status.plan_code}` : ""}
          </Badge>
          {d.plan_status.trial_end && <span className="text-muted-foreground">trial ends {new Date(d.plan_status.trial_end).toLocaleDateString()}</span>}
        </div>
      )}

      {d.seed_note && (
        <div className="card-flat p-3 border-accent/30 text-[11px] text-muted-foreground" data-testid="recon-seed-note">
          <span className="font-semibold text-foreground">Data retention:</span> {d.seed_note}
        </div>
      )}

      {d.skip_reasons.length > 0 && (
        <div className="card-flat p-4" data-testid="recon-skip-reasons">
          <div className="label-eyebrow mb-2">Why rows were skipped</div>
          <div className="space-y-1.5">
            {d.skip_reasons.map((s) => (
              <div key={s.reason} className="flex items-start justify-between gap-3 text-xs" data-testid={`recon-reason-${s.reason}`}>
                <span className="text-muted-foreground">{s.label}</span>
                <span className="font-mono font-semibold shrink-0">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source file</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Analyzed</TableHead>
              <TableHead className="text-right">Committed</TableHead>
              <TableHead className="text-right">Skipped</TableHead>
              <TableHead>Skip reasons</TableHead><TableHead>Committed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.runs.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8 text-sm">No import runs yet.</TableCell></TableRow>
            )}
            {d.runs.map((r) => (
              <TableRow key={r.import_id} data-testid="recon-run-row">
                <TableCell className="text-xs font-medium max-w-[200px] truncate" title={r.filename}>{r.filename}</TableCell>
                <TableCell><Badge variant={STATUS_TONE[r.status] || "outline"} className="text-[9px]">{r.status}</Badge></TableCell>
                <TableCell className="text-right font-mono text-xs">{r.analyzed_rows}</TableCell>
                <TableCell className="text-right font-mono text-xs text-[#00E65B]">{r.committed}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.skipped_count || 0}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(r.skipped_by_reason || []).map((s) => (
                      <Badge key={s.reason} variant="warning" className="text-[8px]" title={s.label}>{s.reason} · {s.count}</Badge>
                    ))}
                    {!(r.skipped_by_reason || []).length && <span className="text-muted-foreground">—</span>}
                  </div>
                </TableCell>
                <TableCell className="text-[10px] font-mono text-muted-foreground">{r.committed_at ? new Date(r.committed_at).toLocaleDateString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {d.runs.some((r) => (r.skipped_rows || []).length > 0) && (
        <details className="card-flat p-4" data-testid="recon-skipped-samples">
          <summary className="cursor-pointer text-xs font-medium">Skipped row samples (per file)</summary>
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {d.runs.flatMap((r) => (r.skipped_rows || []).map((s, i) => (
              <div key={`${r.import_id}-${i}`} className="flex items-start justify-between gap-3 text-[11px] border-b border-border/50 pb-1.5">
                <span className="min-w-0"><span className="text-muted-foreground">{r.filename}{s.sheet ? ` · ${s.sheet}` : ""}:</span> {s.label || "(row)"}</span>
                <Badge variant="outline" className="text-[8px] shrink-0">{s.reason}</Badge>
              </div>
            )))}
          </div>
        </details>
      )}
    </div>
  );
}
